import { WorkerMessageTypes } from 'v2/types'
import { tabLogger } from 'v2/utils/logger'
import { map } from 'lodash'
import { v4 } from 'uuid'

interface TabHeartbeat {
  timestamp: number
  tabId: string
}

/**
 * Manages tab closure detection using SharedWorker heartbeats and localStorage
 * Detects when the last tab is closed to perform cleanup
 */
class TabClosureManager {
  private static instance: TabClosureManager

  private tabId: string

  private worker: SharedWorker | null = null

  private isInitialized = false

  private unloadHandler: (() => void) | null = null

  private constructor() {
    this.tabId = v4()
    tabLogger.debug('TabClosureManager initialized with tabId:', this.tabId)
  }

  public static getInstance(): TabClosureManager {
    if (!TabClosureManager.instance) TabClosureManager.instance = new TabClosureManager()
    return TabClosureManager.instance
  }

  /**
   * Initializes the manager with a SharedWorker instance
   * Sets up heartbeat listeners and cleanup handlers
   */
  public init(worker: SharedWorker): void {
    if (this.isInitialized) return

    try {
      this.worker = worker
      this.setupHeartbeatListener()
      this.setupClosureDetection()
      this.cleanupOldHeartbeats()

      this.isInitialized = true
      tabLogger.debug('TabClosureManager init called')
    } catch (error) {
      tabLogger.error('Error initializing TabClosureManager:', error)
    }
  }

  public destroy(): void {
    try {
      if (this.unloadHandler) {
        window.removeEventListener('beforeunload', this.unloadHandler)
        this.unloadHandler = null
      }

      this.worker?.port.postMessage({ type: WorkerMessageTypes.stopTabHeartbeat })
      localStorage.removeItem(`tab-heartbeat-${this.tabId}`)
      this.isInitialized = false
    } catch (error) {
      tabLogger.error('Error destroying TabClosureManager:', error)
    }
  }

  private setupHeartbeatListener(): void {
    if (!this.worker) return

    try {
      this.worker.port.onmessage = (event) => {
        if (event.data?.type === WorkerMessageTypes.tabHeartbeat) {
          this.updateHeartbeat()
        }
      }

      this.worker.port.postMessage({ type: WorkerMessageTypes.startTabHeartbeat })
    } catch (error) {
      tabLogger.error('Error setting up heartbeat listener:', error)
    }
  }

  /**
   * Updates localStorage with current tab's heartbeat
   * Called every time we receive a heartbeat from worker
   */
  private updateHeartbeat(): void {
    try {
      localStorage.setItem(
        `tab-heartbeat-${this.tabId}`,
        JSON.stringify({
          timestamp: Date.now(),
          tabId: this.tabId,
        }),
      )
    } catch (error) {
      tabLogger.error('Error updating heartbeat:', error)
    }
  }

  /**
   * Sets up beforeunload handler to detect tab closure
   * Checks if this is the last tab being closed
   */
  private setupClosureDetection(): void {
    this.unloadHandler = () => {
      try {
        localStorage.removeItem(`tab-heartbeat-${this.tabId}`)

        // Get snapshot of keys first to avoid race conditions
        const keys = this.getHeartbeatKeys()
        const remainingHeartbeats = this.getActiveHeartbeats(keys)

        if (remainingHeartbeats.length === 0) {
          this.handleLastTabClosure()
        }

        // this.worker?.port.postMessage({ type: WorkerMessageTypes.stopTabHeartbeat })
      } catch (error) {
        tabLogger.error('Error in closure detection:', error)
      }
    }

    window.addEventListener('beforeunload', this.unloadHandler)
  }

  private getHeartbeatKeys(): string[] {
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith('tab-heartbeat-')) {
        keys.push(key)
      }
    }
    return keys
  }

  private getActiveHeartbeats(keys: string[]): TabHeartbeat[] {
    const now = Date.now()

    return map(keys, (key) => {
      try {
        const item = localStorage.getItem(key)
        if (!item) return null

        const heartbeat = JSON.parse(item) as TabHeartbeat
        if (now - heartbeat.timestamp < 5000) return heartbeat

        localStorage.removeItem(key)
        return null
      } catch (error) {
        tabLogger.error('Error processing heartbeat:', error)
        localStorage.removeItem(key)
        return null
      }
    }).filter(Boolean) as TabHeartbeat[]
  }

  private cleanupOldHeartbeats(): void {
    try {
      const keys = this.getHeartbeatKeys()
      this.getActiveHeartbeats(keys) // This will clean up old heartbeats
    } catch (error) {
      tabLogger.error('Error cleaning up old heartbeats:', error)
    }
  }

  private handleLastTabClosure(): void {
    tabLogger.info('Last tab is being closed')
  }
}

export const tabClosureManager = TabClosureManager.getInstance()
